/**
 * Overview data endpoints for liveOverviewApi.
 * Lesson details, student details, flex/liste tabs, contacts.
 */
import { supabase } from '../../../../lib/supabase';
import { liveOverviewApi } from './liveOverviewApi';
import type { LessonDetailRow, StudentDetailRow } from '../types';

const overviewApi = liveOverviewApi.injectEndpoints({
    endpoints: (builder) => ({
        getLessonDetailsForWeek: builder.query<
            LessonDetailRow[],
            { schoolId: string; dates: string[] }
        >({
            async queryFn({ schoolId, dates }) {
                try {
                    if (!schoolId || dates.length === 0) return { data: [] };
                    const { data, error } = await supabase
                        .from('vw_react_lesson_details' as never)
                        .select('period_label, lesson_date, course_name, subject_name, room_number, room_name, teacher_names, course_id')
                        .eq('school_id' as never, schoolId as never)
                        .in('lesson_date' as never, dates as never);
                    if (error) {
                        return { error: { status: 'CUSTOM_ERROR', error: error.message } };
                    }
                    return { data: (data || []) as LessonDetailRow[] };
                } catch (e) {
                    return { error: { status: 'CUSTOM_ERROR', error: String(e) } };
                }
            },
            providesTags: ['LessonDetails'],
            keepUnusedDataFor: 120,
        }),

        getStudentDetails: builder.query<StudentDetailRow[], { studentIds: string[] }>({
            async queryFn({ studentIds }) {
                try {
                    if (studentIds.length === 0) return { data: [] };
                    const { data, error } = await supabase
                        .from('vw_react_student_detail' as never)
                        .select('*')
                        .in('id' as never, studentIds as never);
                    if (error) {
                        return { error: { status: 'CUSTOM_ERROR', error: error.message } };
                    }
                    return { data: (data || []) as StudentDetailRow[] };
                } catch (e) {
                    return { error: { status: 'CUSTOM_ERROR', error: String(e) } };
                }
            },
            providesTags: ['StudentDetails'],
            keepUnusedDataFor: 120,
        }),

        getInitData: builder.query<
            {
                classes: Array<{ id: string; name: string; grade_level: string | null }>;
                allowed_class_ids: string[];
                main_class_ids: string[];
                semesters: Array<{ id: string; name: string; start_date: string; end_date: string; is_active: boolean; is_current: boolean; is_future: boolean }>;
                views: Array<{
                    id: string; view_name: string; visibility: 'private' | 'shared';
                    user_profile_id: string; class_id: string | null;
                    course_id: string | null;
                    column_order: string[]; column_visibility: Record<string, boolean>;
                    column_widths: Record<string, number>; sort_field: string | null;
                    sort_direction: string; group_by: string | null; filters: any[] | null;
                    course_ids: string[] | null;
                    shared_with_users: string[] | null; shared_with_groups: string[] | null;
                    updated_at: string;
                }>;
                courses: Array<{ id: string; name: string }>;
            },
            { profileId: string; schoolId: string }
        >({
            async queryFn({ profileId, schoolId }) {
                try {
                    const { data, error } = await supabase.rpc(
                        'fn_klassenuebersicht_init' as never,
                        { p_user_profile_id: profileId, p_school_id: schoolId } as never
                    );
                    if (error) {
                        return { error: { status: 'CUSTOM_ERROR', error: error.message } };
                    }
                    const envelope = data as any || {};
                    return {
                        data: {
                            classes: envelope.classes || [],
                            allowed_class_ids: envelope.allowed_class_ids || [],
                            main_class_ids: envelope.main_class_ids || [],
                            semesters: envelope.semesters || [],
                            views: envelope.views || [],
                            courses: envelope.courses || [],
                        }
                    };
                } catch (e) {
                    return { error: { status: 'CUSTOM_ERROR', error: String(e) } };
                }
            },
            providesTags: ['InitData'],
            keepUnusedDataFor: 600,
        }),

        getFlexOverview: builder.query<
            {
                students: Array<{
                    id: string; first_name: string; last_name: string;
                    pickup: Record<string, string>;
                    enrollments: Record<string, { course_name: string }>;
                }>;
                selected_class_id: string | null;
            },
            { classId?: string; semesterId?: string }
        >({
            async queryFn({ classId, semesterId }) {
                try {
                    const params: Record<string, unknown> = {};
                    if (classId) params.p_class_id = classId;
                    if (semesterId) params.p_semester_id = semesterId;
                    const { data, error } = await supabase.rpc(
                        'fn_klassenuebersicht_flex' as never,
                        params as never
                    );
                    if (error) {
                        return { error: { status: 'CUSTOM_ERROR', error: error.message } };
                    }
                    const envelope = data as any || {};
                    return {
                        data: {
                            students: envelope.students || [],
                            selected_class_id: envelope.selected_class_id || null,
                        }
                    };
                } catch (e) {
                    return { error: { status: 'CUSTOM_ERROR', error: String(e) } };
                }
            },
            providesTags: (_r, _e, { classId }) => [{ type: 'FlexOverview', id: classId || 'init' }],
            keepUnusedDataFor: 60,
        }),

        getListeOverview: builder.query<
            Array<{
                id: string; first_name: string; last_name: string;
                class_name: string | null; class_id: string | null;
                but_type: string | null; but_valid_until: string | null;
                photo_permissions: Record<string, boolean> | null;
            }>,
            { classId: string | null }
        >({
            async queryFn({ classId }) {
                try {
                    const params: Record<string, unknown> = {};
                    if (classId) params.p_class_id = classId;
                    const { data, error } = await supabase.rpc(
                        'fn_klassenuebersicht_liste' as never,
                        params as never
                    );
                    if (error) {
                        return { error: { status: 'CUSTOM_ERROR', error: error.message } };
                    }
                    return { data: (data || []) as any[] };
                } catch (e) {
                    return { error: { status: 'CUSTOM_ERROR', error: String(e) } };
                }
            },
            providesTags: (_r, _e, { classId }) => [{ type: 'ListeOverview', id: classId || 'ALL' }],
            keepUnusedDataFor: 60,
        }),

        getStudentContacts: builder.query<
            {
                guardians: Array<{
                    profile_id: string;
                    first_name: string;
                    last_name: string;
                    relationship: string | null;
                    authorized_for_pickup: boolean;
                    pickup_priority: number | null;
                    is_primary_guardian: boolean;
                    is_primary_contact: boolean;
                    contacts: Array<{ id: string; type: string; label: string | null; value: string; is_primary: boolean; status: string | null }>;
                }>;
                siblings: Array<{
                    id: string;
                    first_name: string;
                    last_name: string;
                    class_name: string | null;
                }>;
            },
            { studentId: string }
        >({
            async queryFn({ studentId }) {
                try {
                    if (!studentId) return { data: { guardians: [], siblings: [] } };
                    const { data, error } = await supabase.rpc(
                        'fn_klassenuebersicht_student_contacts' as never,
                        { p_student_id: studentId } as never
                    );
                    if (error) {
                        return { error: { status: 'CUSTOM_ERROR', error: error.message } };
                    }
                    const envelope = data as any || {};
                    return {
                        data: {
                            guardians: envelope.guardians || [],
                            siblings: envelope.siblings || [],
                        }
                    };
                } catch (e) {
                    return { error: { status: 'CUSTOM_ERROR', error: String(e) } };
                }
            },
            keepUnusedDataFor: 120,
        }),
    }),
    overrideExisting: false,
});

export const {
    useGetLessonDetailsForWeekQuery,
    useGetStudentDetailsQuery,
    useGetInitDataQuery,
    useGetFlexOverviewQuery,
    useGetListeOverviewQuery,
    useGetStudentContactsQuery,
} = overviewApi;
